/// <reference path="../../../node_modules/vite/types/importMeta.d.ts" />

import { set } from "firebase/database";
import { createReference } from "./lib/firebase";
import { HostAuthenticator } from "./models/HostAuthenticator";
import { AllVotersStore, VoterStore } from "./models/VoterStore";
import { VotingSessionStore } from "./models/VotingSessionStore";

const FOUR_HOURS = 4 * 60 * 60 * 1000;
const IS_DEVELOPMENT = (import.meta.env?.MODE || process.env.NODE_ENV) === "development";

// GET EVENT DATA
// -------------

interface GetEventDataOptions {
  eventId?: string | null;
  date?: Date;
  mode?: "development" | "production";
}

export function getEventStore({
  eventId,
  date = new Date(),
  mode = IS_DEVELOPMENT ? "development" : "production",
}: GetEventDataOptions = {}) {
  const eventPrefix = [
    // Prefix the key with the event date, so each event has its own data
    getEventDateString(date),

    // Optionally add a event id to the prefix
    // This allows for multiple events to be run at the same time
    eventId,

    // Separate dev and prod data
    mode === "development" ? "dev" : null,
  ]
    .filter(Boolean)
    .join("-");

  return {
    eventKey: eventPrefix,

    // Host
    host: new HostAuthenticator(),

    // Voters
    allVoters: new AllVotersStore({ eventPrefix }),
    voter: (voterId: string) => new VoterStore({ eventPrefix, voterId }),

    // Voting Session
    votingSession: new VotingSessionStore({ eventPrefix }),

    // Utils
    async resetDatabase() {
      await set(createReference(eventPrefix), null);
    },
  };
}

// TYPES
// -----

export type EventStore = ReturnType<typeof getEventStore>;
export type { Store } from "./lib/Store";
export type { GroupedPresenceData, PresenceData } from "./models/PresenceStore";
export { VoteOption, voteOptions } from "./models/VoterStore";
export type { VoterData, VotersById } from "./models/VoterStore";
export { VotingState, votingStates } from "./models/VotingSessionStore";
export type { VotingSessionData } from "./models/VotingSessionStore";

// UTILS
// -----

function getEventDateString(date: Date) {
  // Date starts at 4am instead of 12am just be safe to not break events that go past midnight
  return new Date(date.getTime() - FOUR_HOURS).toISOString().split("T")[0];
}
